import {
  mapChipFieldOptions,
  mapToggleFieldOptions,
} from 'features/consults/utils/formFields';
import {
  neuroPlanFields as fields,
  thrombolyticValues,
} from 'features/consults/shared/tabs/Plan/formFields';
import hpiFields from 'features/consults/shared/tabs/HPI/formFields';
import iviaFields from 'features/consults/shared/tabs/IVIA/formFields';

export const thrombolyticElement = [
  mapToggleFieldOptions(fields.thrombolyticInterventionPredefined),
];

const thrombolyticExclusion = [
  mapChipFieldOptions(
    fields.thrombolyticExclusionLessThanThreeHourWindowPredefined,
  ),
  fields.thrombolyticExclusionLessThanThreeHourWindowFreeText,
  mapChipFieldOptions(
    fields.thrombolyticExclusionThreeToFourPointFiveHourWindowPredefined,
  ),
  mapToggleFieldOptions(fields.thrombolyticExclusionPredefined),
  fields.thrombolyticExclusionFreeText,
];

// Extended Window Thrombolytic Exclusion (ivIa slice) — shown only with the
// IA-oriented Thrombolytic/Intervention selections below.
export const extendedWindowExclusionElements = [
  mapChipFieldOptions(iviaFields.extendedWindowThrombolyticExclusionsPredefined),
  iviaFields.extendedWindowThrombolyticExclusionsFreeText,
];

export const extendedWindowExclusionThrombolyticValues = [
  thrombolyticValues.IAIntervention,
  thrombolyticValues.NOTIVThrombolyticOrIAIntervention,
  thrombolyticValues.PossibleIAcandidate,
  thrombolyticValues.IVThrombolyticPossibleIACandidate,
];

export const thrombolyticElements = {
  [thrombolyticValues.IVThrombolytic]: [
    mapToggleFieldOptions(fields.dose),
    mapChipFieldOptions(fields.iaExclusionPredefined),
    fields.iaExclusionFreeText,
    fields.timeIVThrombolyticRecommendedMountainTime,
    mapChipFieldOptions(
      fields.thrombolyticAdministrationRecommendationsPredefined,
    ),
    fields.thrombolyticAdministrationRecommendationsFreeText,
    mapChipFieldOptions(fields.tnkDelayReasonsPredefined),
    fields.tnkDelayReasonFreeText,
  ],
  // Same sub-sections as "IV thrombolytic" per 2026 AIS guidelines.
  [thrombolyticValues.IVThrombolyticExtendedWindow]: [
    mapToggleFieldOptions(fields.dose),
    mapChipFieldOptions(fields.iaExclusionPredefined),
    fields.iaExclusionFreeText,
    fields.timeIVThrombolyticRecommendedMountainTime,
    mapChipFieldOptions(
      fields.thrombolyticAdministrationRecommendationsPredefined,
    ),
    fields.thrombolyticAdministrationRecommendationsFreeText,
    mapChipFieldOptions(fields.tnkDelayReasonsPredefined),
    fields.tnkDelayReasonFreeText,
  ],
  [thrombolyticValues.IAIntervention]: [
    ...thrombolyticExclusion,
    fields.timeIAInterventionRecommended,
    mapChipFieldOptions(fields.iaDecisionMakingPredefined),
    fields.iADecisionMakingFreeText,
    mapChipFieldOptions(fields.iaManagementRecommendations),
    fields.iaManagementRecommendationsFreeText,
  ],
  [thrombolyticValues.IVAndIAIntervention]: [
    mapToggleFieldOptions(fields.dose),
    fields.timeIVThrombolyticRecommendedMountainTime,
    fields.timeIAInterventionRecommended,
    mapChipFieldOptions(fields.iaDecisionMakingPredefined),
    fields.iADecisionMakingFreeText,
    mapChipFieldOptions(fields.iaManagementRecommendations),
    fields.iaManagementRecommendationsFreeText,
    mapChipFieldOptions(
      fields.thrombolyticAdministrationRecommendationsPredefined,
    ),
    fields.thrombolyticAdministrationRecommendationsFreeText,
    mapChipFieldOptions(fields.tnkDelayReasonsPredefined),
    fields.tnkDelayReasonFreeText,
  ],
  [thrombolyticValues.NOTIVThrombolyticOrIAIntervention]: [
    ...thrombolyticExclusion,
    mapChipFieldOptions(fields.iaExclusionPredefined),
    fields.iaExclusionFreeText,
  ],
  [thrombolyticValues.PossibleIAcandidate]: [
    ...thrombolyticExclusion,
    mapChipFieldOptions(fields.possibleIACandidatePredefined),
    fields.possibleIACandidateFreeText,
    mapChipFieldOptions(fields.iaDecisionMakingPredefined),
    fields.iADecisionMakingFreeText,
    mapChipFieldOptions(fields.iaManagementRecommendations),
    fields.iaManagementRecommendationsFreeText,
  ],
  [thrombolyticValues.IVThrombolyticPossibleIACandidate]: [
    mapToggleFieldOptions(fields.dose),
    mapChipFieldOptions(fields.possibleIACandidatePredefined),
    fields.possibleIACandidateFreeText,
    mapChipFieldOptions(fields.iaDecisionMakingPredefined),
    fields.iADecisionMakingFreeText,
    mapChipFieldOptions(fields.iaManagementRecommendations),
    fields.iaManagementRecommendationsFreeText,
    fields.timeIVThrombolyticRecommendedMountainTime,
    mapChipFieldOptions(
      fields.thrombolyticAdministrationRecommendationsPredefined,
    ),
    fields.thrombolyticAdministrationRecommendationsFreeText,
    mapChipFieldOptions(fields.tnkDelayReasonsPredefined),
    fields.tnkDelayReasonFreeText,
  ],
};

export const intraarterialExclusionElements = [
  mapChipFieldOptions(fields.thrombolyticIntraarterialExclusionPredefined),
];

export const intraarterialExclusionThrombolyticValues = [
  thrombolyticValues.NOTIVThrombolyticOrIAIntervention,
];

export const getVisiblePlanFields = (thrombolyticValue) => [
  ...(thrombolyticElements[thrombolyticValue] ?? []),
  ...(intraarterialExclusionThrombolyticValues.includes(thrombolyticValue)
    ? intraarterialExclusionElements
    : []),
];

// Appears for ALL Thrombolytic/Intervention selections (left column, ivIa slice).
export const relativeContraindicationsElements = [
  mapToggleFieldOptions(iviaFields.thrombolyticRelativeContraindicationRisk),
  mapChipFieldOptions(iviaFields.thrombolyticRelativeContraindicationsPredefined),
  iviaFields.thrombolyticRelativeContraindicationsFreeText,
];

// Moved from the HPI tab — stays on the HPI data slice.
// "no stroke in last 3 months" is retired under the 2026 AIS guidelines: hidden
// from new consults, but kept in the field definition so historical consults and
// reports still render it (and so the multi-chip summary can resolve the value).
export const candidateElements = [
  mapChipFieldOptions({
    ...hpiFields.possibleAlteplaseCandidatePredefined,
    options: hpiFields.possibleAlteplaseCandidatePredefined.options.filter(
      ({ value }) => value !== 'NoStrokeInLast3Months',
    ),
  }),
  hpiFields.possibleAlteplaseCandidateFreeText,
];

// Right column, ivIa data slice — sits under the moved "Possible Thrombolytic
// Candidate" (HPI slice) panel.
// "Time last known well timestamp" is retired: it is not a selection providers
// make when determining extended window eligibility. Hidden from new consults,
// but kept in the field definition so historical consults and reports still
// render it (and so the multi-chip summary can resolve the value).
export const rightColumnElements = [
  mapChipFieldOptions({
    ...iviaFields.possibleExtendedWindowThrombolyticCandidatePredefined,
    options:
      iviaFields.possibleExtendedWindowThrombolyticCandidatePredefined.options.filter(
        ({ value }) => value !== 'TimeLastKnownWellTimestamp',
      ),
  }),
  iviaFields.possibleExtendedWindowThrombolyticCandidateFreeText,
  mapToggleFieldOptions(iviaFields.disabilityDiscussion),
  iviaFields.disabilityDiscussionFreeText,
];
